<template>
  <div class="datagrid-component" v-cloak>
    <div class="datagrid-header">
        <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
                <slot name="title"></slot>
            </div>

            <div>
                <slot name="page-actions"
                    :filters="filtersMapped"
                    :filtersUrlEncoded="filtersUrlEncoded"
                    :selectedItems="selectedItems"
                    :selectedItemsObjects="selectedItemsObjects"
                    :sortingColumn="sortingColumn"
                    :sortingDirection="sortingDirection"
                    :options="options"
                    :inputParams="inputParams"></slot>
            </div>
        </div>


        <div class="filter-panel-wrapper" :class="{ 'justify-content-end': !options.allowSearchbar && filters.length === 0}">
            <filter-panel
                v-if="options.allowSearchbar || filters.length > 0"
                :search="search"
                :filters="filters"
                :onchange="filterOnChange"
                :disabled="loading"
                :allow-search="options.allowSearchbar"
            ></filter-panel>

            <div class="filter-panel-additional" :class="{'disabled': loading}">
                <slot name="filter-additional" :inputParams="inputParams" :changeInputParams="changeInputParams"></slot>
            </div>
        </div>

        <div class="columns-picker" :class="{'hidden': loading}">
            <dropdown-multichoice
                ref="datagridColumnsPicker"
                type="icon-button"
                name="columns_picker"
                icon="fal fa-cog"
                :title="trans('columns-picker-title')"
                :options="columnsConfigDropdownMapped"
                :checked="columnsVisibleMapped"
                :hideCountBadge="true"
                :alignRight="true"
                :onsubmit="columnsOnChange"
                :onreset="columnsOnChange"
            ></dropdown-multichoice>
        </div>
    </div>

    <div class="datagrid-body-wrapper">
      <div :class="{'loading': loading}">
        <div class="datagrid-body" :style="{'max-height': gridMaxHeight + 'px'}">
            <table class="table">
                <thead>
                    <tr>
                        <th class="checkbox-column" v-if="batchActionsSet && data.length > 0">
                            <div class="checkbox-inner-wrapper">
                                <input type="checkbox" class="form-check-input" id="checkbox_batch_all_top" :checked="selectedItems.length === data.length" @change="batchCheckboxCheckAll">
                                <label class="form-check-label" for="checkbox_batch_all_top"></label>
                            </div>
                        </th>
                        <th v-for="column in columnsVisible"
                            :title="column.label"
                            @click="sortBy(column)"
                            :class="{ 'sortable-column': column.sortType !== '', 'active': column.name === sortingColumn, 'fixed-left': isFixedColumn(column), 'fixed-left-last': isLastFixedColumn(column) }"
                            :style="fixedColumnStyle(column, {'min-width': column.minWidth ? column.minWidth + 'px' : 'auto', 'max-width': column.maxWidth ? column.maxWidth + 'px' : 'auto'})"
                        >
                            <div class="d-flex justify-content-start">
                                <span class="column-label">{{ column.label }}</span>
                                <span class="sorting-indicator">
                                    <i class="fad fa-fw fa-sort" v-if="column.name !== sortingColumn"></i>
                                    <i class="fad fa-fw"
                                         v-if="column.name === sortingColumn"
                                        :class="{ 'fa-sort-down': sortingDirection === 'desc', 'fa-sort-up': sortingDirection === 'asc' }"></i>
                                </span>
                            </div>
                        </th>
                        <th class="actions-column fixed" :class="{'mobile': !isDesktopResolution, 'minSize': !options.actionColumnFixed}">
                            <!-- placeholder for columns picker which have to be outside of datagrid-body and table due to sticky+overflow issues -->
                            <div class="columns-picker-placeholder"></div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, idx) in sortedData" :class="getHighlightedClasses(idx)">
                        <td class="checkbox-column" v-if="batchActionsSet">
                            <div class="form-check">
                                <input type="checkbox" class="form-check-input" :id="'checkbox_'+item.id" :value="item.id" @change="rowCheckboxCheck(item.id)">
                                <label class="form-check-label" :for="'checkbox_'+item.id"></label>
                            </div>
                        </td>
                        <td v-for="column in columnsVisible"
                            :class="{ 'fixed-left': isFixedColumn(column), 'fixed-left-last': isLastFixedColumn(column) }"
                            :style="fixedColumnStyle(column)"
                        >
                            <slot :name="'row-td-formatter-' + column.name" :item="item" :options="options" :column="column" :inputParams="inputParams">
                                <!-- fallback na catch-all -->
                                <slot name="row-td-formatter" :item="item" :options="options" :column="column" :inputParams="inputParams">
                                    <!-- úplne posledný fallback (aktuálne chovanie) -->
                                    <div :class="'type-'+column.sortType">
                                        <span v-if="column.sortType === 'money' && item[column.name]">{{ item[column.name] | formatMoney(options.currencySymbol) }}</span>
                                        <span v-else-if="column.sortType === 'money'">&ndash;</span>
                                        <span v-else>{{ item[column.name] }}</span>
                                    </div>
                                </slot>
                          </slot>
                        </td>
                        <td class="actions-column" :class="{'mobile': !isDesktopResolution, 'fixed': options.actionColumnFixed}">
                            <span class="gradient"></span>
                            <div v-if="$scopedSlots['row-actions']">
                                <div v-if="isDesktopResolution">
                                    <slot name="row-actions" :item="item" :options="options" :inputParams="inputParams"></slot>
                                </div>
                                <div class="btn-group" v-else>
                                    <button type="button" class="btn btn-light dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                        <i class="fal fa-fw fa-ellipsis-v"></i>
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <slot name="row-actions" :item="item" :options="options" :inputParams="inputParams"></slot>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="!data || data.length === 0">
                        <td :colspan="columnsVisible.length + 2" class="no-data">
                            <div class="msg" v-if="!loading">{{ isFirstLoaded ? trans('label-no-data') : '' }}</div>
                        </td>
                    </tr>
                </tbody>
                <!--
                    Totals row ("Spolu"): opt-in per grid via the summary-row prop, sums every
                    column flagged summable server-side. It sums the checked rows when there is
                    a selection and all displayed rows otherwise, so a grid with row checkboxes
                    gets subset totals for free. Values are rendered through the very same
                    row-td-formatter slots as the data rows, so money/hours formatting can not
                    drift between a cell and its total.
                -->
                <tfoot v-if="summaryRow && data && data.length > 0">
                    <tr class="summary-row" :class="{'is-selection': selectedItems.length > 0}">
                        <td class="checkbox-column" v-if="batchActionsSet"></td>
                        <td class="summary-label-column" :colspan="summaryLabelColspan"
                            :class="{ 'fixed-left': summaryLabelFixed, 'fixed-left-last': summaryLabelFixed }"
                            :style="summaryLabelFixed ? {'left': '0px'} : {}">
                            <div class="summary-label">
                                <span class="summary-title">
                                    {{ selectedItems.length > 0 ? trans('summary-selection-label') : trans('summary-label') }}
                                </span>
                                <span class="summary-count">{{ summaryCountLabel }}</span>
                            </div>
                        </td>
                        <td v-for="column in summaryValueColumns">
                            <template v-if="column.summable">
                                <slot :name="'row-td-formatter-' + column.name" :item="summaryItem" :options="options" :column="column" :inputParams="inputParams">
                                    <slot name="row-td-formatter" :item="summaryItem" :options="options" :column="column" :inputParams="inputParams">
                                        <div :class="'type-'+column.sortType">
                                            <span v-if="summaryItem[column.name] !== null">{{ summaryItem[column.name] }}</span>
                                            <span v-else>&ndash;</span>
                                        </div>
                                    </slot>
                                </slot>
                            </template>
                        </td>
                        <td class="actions-column" :class="{'mobile': !isDesktopResolution, 'fixed': options.actionColumnFixed}">
                            <span class="gradient"></span>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
      </div>
    </div>

    <div class="datagrid-footer">
        <div class="total-rows-panel">
             {{ trans('total-rows') }}: <b>{{ isFirstLoaded ? data.length : '?' }}</b>
        </div>

        <div class="batch-actions-panel" v-if="selectedItems.length > 0">
            <span class="mr-4">{{ trans('selected-rows') }}: <b>{{ selectedItems.length }}</b></span>
            <slot name="batch-actions" :selectedItems="selectedItems" :selectedItemsObjects="selectedItemsObjects"></slot>
        </div>
    </div>

    <slot name="extra-logic" :refresh="refresh" :inputParams="inputParams"></slot>
  </div>
</template>

<script>
import FilterPanel from "./FilterPanel.vue";
import TranslationsUtils from "../../app-components/TranslationsUtils";
import { sortData } from "../../utils";

Vue.component('filter-panel', FilterPanel);

    export default {
        mixins: [TranslationsUtils],
        props: {
            'resource': { type: Object },
            'defaultSortColumn': { type: String },
            'defaultSortDirection': { type: String, default: 'asc' },
            // renders the totals ("Spolu") row; the grid's columns must carry the summable flag
            'summaryRow': { type: Boolean, default: false },
            // what is being counted, in the 3 Slovak/Czech plural forms: { one, few, many }
            'summaryItemLabels': { type: Object, default: null },
        },

        data() {
            return {
                'uniqueKey': this.resource.uniqueName,
                'listingRoute': this.resource.listingRoute,
                'componentName': 'datagrid', // this is used in TranslationUtils
                'columnsVisible': this.resource.selectedColumns, // user-defined or ALL (if user does not have defined for this unique-key grid)
                'columns': this.resource.columns,
                'filters': this.resource.filters,
                'search': this.resource.search,
                'inputParams': this.resource.inputParams,
                'highlighted': this.resource.highlighted,
                'options': this.resource.options,
                'loading': false,
                'data': this.resource.data,
                'gridMaxHeight': 100,
                'gridFooterOffset': 115,
                'selectedItems': [],
                'sortingColumn': this.defaultSortColumn,
                'sortingDirection': this.defaultSortDirection,
                'isLazyLoading': this.resource.isLazyLoading,
                'isFirstLoaded': false,
                // column name => left offset (px) of the columns pinned to the left edge
                // (options.fixedColumns); measured from the rendered header, see updateFixedColumnOffsets()
                'fixedColumnOffsets': {}
            };
        },

        mounted() {
            // recalculate height on first load + every resize event
            const datagridBody = this.$el.querySelector('.datagrid-body');
            if (datagridBody) {
                this.gridMaxHeight = window.innerHeight - datagridBody.offsetTop - this.gridFooterOffset;
            }

            window.addEventListener('resize', () => {
                this.gridMaxHeight = window.innerHeight - datagridBody.offsetTop - this.gridFooterOffset;
                this.updateFixedColumnOffsets();
            });

            // pinned columns keep their offsets in sync with whatever the table lays out
            // (data/visibility/width changes) - the header row is the single source of truth
            if (typeof ResizeObserver !== 'undefined') {
                const table = this.$el.querySelector('.datagrid-body .table');
                if (table) {
                    new ResizeObserver(() => this.updateFixedColumnOffsets()).observe(table);
                }
            }
            this.$nextTick(() => this.updateFixedColumnOffsets());

            // load saved sorting state
            if (sessionStorage.getItem(this.sessionSortingKeyName) !== null) {
                let storedState = JSON.parse(sessionStorage.getItem(this.sessionSortingKeyName));

                if(storedState.sortingColumn && storedState.sortingDirection) {
                    this.sortingColumn = storedState.sortingColumn;
                    this.sortingDirection = storedState.sortingDirection;
                }
            }

            if(!this.isFirstLoaded && this.isLazyLoading) {
                this.refresh(() => {
                    this.isFirstLoaded = true;
                });
            } else {
                this.isFirstLoaded = true;
            }

        },

        methods: {
            isFixedColumn(column) {
                return this.fixedColumnNames.includes(column.name);
            },

            isLastFixedColumn(column) {
                return this.isFixedColumn(column) && this.lastFixedColumnName === column.name;
            },

            fixedColumnStyle(column, base = {}) {
                if (!this.isFixedColumn(column)) return base;

                return Object.assign({}, base, { 'left': (this.fixedColumnOffsets[column.name] || 0) + 'px' });
            },

            /**
             * Left-pinned columns are position: sticky, which needs an explicit `left` per column.
             * Walk the header cells in DOM order and accumulate the widths of everything that is
             * itself pinned (checkbox column + earlier fixed columns), so every pinned column sits
             * flush against the previous one whatever their rendered widths turn out to be.
             */
            updateFixedColumnOffsets() {
                if (this.fixedColumnNames.length === 0) return;

                const headerCells = this.$el.querySelectorAll('.datagrid-body .table thead tr:first-child th');
                const offsets = {};
                let left = 0;
                let visibleIdx = 0;

                headerCells.forEach(th => {
                    if (th.classList.contains('checkbox-column')) {
                        left += th.offsetWidth;
                        return;
                    }
                    if (th.classList.contains('actions-column')) return;

                    const column = this.columnsVisible[visibleIdx++];
                    if (!column || !this.isFixedColumn(column)) return;

                    offsets[column.name] = left;
                    left += th.offsetWidth;
                });

                const changed = Object.keys(offsets).some(name => offsets[name] !== this.fixedColumnOffsets[name])
                    || Object.keys(offsets).length !== Object.keys(this.fixedColumnOffsets).length;
                if (changed) {
                    this.fixedColumnOffsets = offsets;
                }
            },

            rowCheckboxCheck(itemId) {
                if (this.selectedItems.includes(itemId)) {
                    this.selectedItems.splice(this.selectedItems.indexOf(itemId), 1);
                } else {
                    this.selectedItems.push(itemId);
                }

                // race condition, because we need to wait for vue to draw an element
                setTimeout(() => {
                    let chb = this.$el.querySelector('#checkbox_batch_all');
                    if(!chb) return; // in case, vue still not drawed element, this will prevent error

                    if (this.selectedItems.length === 0) {
                        chb.checked = false;
                    } else if (this.selectedItems.length === this.data.length) {
                        chb.checked = true;
                    } else {
                        chb.indeterminate = true;
                    }
                }, 300);
            },

            batchCheckboxCheckAll(evt) {
                let allChecks = this.$el.querySelectorAll('tbody tr td.checkbox-column .form-check-input');
                for(let x in allChecks) {
                    if(typeof allChecks[x] !== 'object') continue;

                    allChecks[x].checked = evt.target.checked;
                }

                if (evt.target.checked) {
                    this.selectedItems = this.data.map(x => x.id);
                } else {
                    this.selectedItems = [];
                }
            },

            batchCancelSelection() {
                this.selectedItems = [];

                let allChecks = this.$el.querySelectorAll('tbody tr td.checkbox-column .form-check-input');
                for(let x in allChecks) {
                    if(typeof allChecks[x] !== 'object') continue;

                    allChecks[x].checked = false;
                }
            },

            refresh(callback) {
                this.loading = true;
                let routeAdditional = Object.keys(this.inputParams).length > 0 ? '?ip='+JSON.stringify(this.inputParams) : '';

                axios.get(this.listingRoute+routeAdditional)
                    .then((response) => {
                        if(response && response.data && response.data.uniqueName === this.uniqueKey) {
                            this.data = response.data.data;
                            this.search = response.data.search;
                            this.filters = response.data.filters;
                            this.columns = response.data.columns;
                            this.columnsVisible = response.data.selectedColumns;

                            this.$nextTick(() => {
                                this.$refs.datagridColumnsPicker.updateCheckedItems(this.columnsVisibleMapped);
                                this.updateFixedColumnOffsets();
                            });
                            this.batchCancelSelection();

                            if(typeof callback === "function") {
                                callback(response.data);
                            }
                        }
                    })
                    .catch(error => {
                        this.$notify({
                          type: 'error',
                          title: 'Error!',
                          text: error.response && error.response.data.message ? error.response.data.message : 'An error has occured.'
                        });
                    })
                    .finally(() => this.loading = false);
            },

            filterOnChange(searchValue, filtersValues) {
                this.loading = true;

                const filterTransformed = Object.fromEntries(
                    Object.entries(filtersValues).map(([key, { key: keyStr, values }]) => [keyStr, values])
                );

                axios.post('/datagrid/set-filter', {
                    'datagrid': this.uniqueKey,
                    'search': searchValue,
                    'filter': filterTransformed
                })
                    .then((response) => {
                        this.refresh();
                    })
                    .catch(error => {
                        this.loading = false;
                        this.$notify({
                          type: 'error',
                          title: 'Error!',
                          text: error.response.data.message ? error.response.data.message : 'An error has occured.'
                        });
                    });
            },

            columnsOnChange(selectedColumns) {
                this.loading = true;

                axios.post('/datagrid/set-columns', {
                    'datagrid': this.uniqueKey,
                    'columns': selectedColumns,
                })
                    .then((response) => {
                        this.refresh();
                    })
                    .catch(error => {
                        this.loading = false;
                        this.$notify({
                          type: 'error',
                          title: 'Error!',
                          text: error.response.data.message ? error.response.data.message : 'An error has occured.'
                        });
                    });
            },

            sortBy(column) {
                if(!column.sortType) {
                    return;
                }

                if(column.name === this.sortingColumn) {
                    this.sortingDirection = this.sortingDirection === 'asc' ? 'desc' : 'asc';
                } else {
                    this.sortingDirection = 'asc';
                }

                this.sortingColumn = column.name;

                // keep saved state in session storage
                sessionStorage.setItem(this.sessionSortingKeyName, JSON.stringify({
                    'sortingColumn': this.sortingColumn,
                    'sortingDirection': this.sortingDirection
                }));
            },

            getHighlightedClasses(idx) {
                if(!this.highlighted) return '';

                let classes = [];

                for(let x in this.highlighted) {
                    if(this.highlighted[x].indexOf(idx) >= 0) {
                        classes.push('highlight-' + x);
                    }
                }

                if(classes.length > 0) {
                    classes.push('highlighted');
                };

                return classes.join(' ');
            },

            changeInputParams(newParams, callback) {
                if (typeof newParams === 'object' && newParams !== null) {
                    Object.assign(this.inputParams, newParams);
                    this.refresh();

                    if(typeof callback === "function") {
                        callback();
                    }
                } else {
                  console.error('Datagrid.changeInputParams(): Expected object, but given: ', newParams);
                }
            }
        },

        computed: {

            isDesktopResolution: {
                get() {
                    return window.screen.width >= 990; // more than max tablet resolutions
                }
            },

            sortedData: {
                get() {
                    let column = this.sortingColumn;
                    let columnObj = this.columnsVisible.find(x => x.name === column);

                    if(!columnObj) return this.data;

                    return sortData(this.data, column, columnObj.sortType, this.sortingDirection);
                },
                set() {}
            },

            columnsConfigDropdownMapped: {
                get() {
                    return this.columns.map(x => {
                        return {
                            'label': x.label,
                            'value': x.name,
                        };
                    });
                },
                set() {}
            },

            columnsVisibleMapped: {
                get() {
                    return this.columnsVisible.map(x => x.name);
                },
                set() {}
            },

            sessionSortingKeyName: {
                get() {
                    return 'sortingState-' + this.uniqueKey;
                },
                set() {}
            },

            filtersMapped: {
                get() {
                    // map to key => values format
                    return this.filters.reduce((acc, item) => { acc[item.key] = item.values; return acc; }, {});
                },
                set() {}
            },
            filtersUrlEncoded: {
                get() {
                    return Object.keys(this.filtersMapped).map(key => key + '=' + this.filtersMapped[key].join(',')).join('&');
                },
                set() {}
            },

            batchActionsSet: {
                get() {
                    return !!this.$scopedSlots['batch-actions'];
                },
                set() {}
            },

            /** Names of the columns pinned to the left edge (grid option fixedColumns), visible ones only. */
            fixedColumnNames: {
                get() {
                    const wanted = Array.isArray(this.options.fixedColumns) ? this.options.fixedColumns : [];
                    return this.columnsVisible.filter(c => wanted.includes(c.name)).map(c => c.name);
                },
                set() {}
            },

            lastFixedColumnName: {
                get() {
                    return this.fixedColumnNames.length > 0 ? this.fixedColumnNames[this.fixedColumnNames.length - 1] : null;
                },
                set() {}
            },

            /** The merged "Spolu" cell is pinned when it covers exactly the pinned columns. */
            summaryLabelFixed: {
                get() {
                    if (this.fixedColumnNames.length === 0) return false;

                    const leading = this.columnsVisible.slice(0, this.summaryLabelColspan).map(c => c.name);
                    return leading.every(name => this.fixedColumnNames.includes(name));
                },
                set() {}
            },

            selectedItemsObjects: {
                get() {
                    return this.data.filter(item => this.selectedItems.includes(item.id));
                },
                set() {}
            },

            /** Rows the totals row adds up: the checked ones, or everything displayed. */
            summaryRows: {
                get() {
                    return this.selectedItems.length > 0 ? this.selectedItemsObjects : this.data;
                },
                set() {}
            },

            /**
             * The leading run of non-summable columns (personal number + name in the labor cost
             * grid) is merged into one cell carrying the "Spolu" label. If the very first column
             * is summable there is nothing to merge into, so the label takes a single cell.
             */
            summaryLabelColspan: {
                get() {
                    let span = 0;
                    for (const column of this.columnsVisible) {
                        if (column.summable) break;
                        span++;
                    }

                    return Math.max(1, span);
                },
                set() {}
            },

            summaryValueColumns: {
                get() {
                    return this.columnsVisible.slice(this.summaryLabelColspan);
                },
                set() {}
            },

            /**
             * A row-shaped object (column name => total) so the totals can be pushed through the
             * grid's existing per-column formatter slots. null = no row contributed a number.
             */
            summaryItem: {
                get() {
                    const totals = {};

                    this.summaryValueColumns.forEach(column => {
                        if (!column.summable) {
                            totals[column.name] = null;
                            return;
                        }

                        let sum = null;

                        this.summaryRows.forEach(row => {
                            const value = parseFloat(row[column.name]);
                            if (isNaN(value)) return; // '-' (not applicable) / null (missing input)

                            sum = (sum === null ? 0 : sum) + value;
                        });

                        // float noise guard: hours/money are never meaningful beyond 4 decimals
                        totals[column.name] = sum === null ? null : Math.round(sum * 10000) / 10000;
                    });

                    return totals;
                },
                set() {}
            },

            summaryCountLabel: {
                get() {
                    const count = this.summaryRows.length;
                    const labels = this.summaryItemLabels;

                    if (!labels) {
                        return String(count);
                    }

                    let form = 'many';
                    if (count === 1) form = 'one';
                    else if (count >= 2 && count <= 4) form = 'few';

                    return count + ' ' + (labels[form] || labels.many || '');
                },
                set() {}
            }
        },
    }
</script>


<style lang="scss">
    // override problematic overflows preventing proper position: sticky functioning  - probably not needed (it was used during development)
    /*.app-body, main, #app { overflow-x: initial !important; overflow: initial !important; }*/
    main { padding-bottom: 0 !important; }

    .search-wrapper {
        .form-control {
            min-width: 100px;
        }
    }

    .filter-panel-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: end;

        .filter-panel-additional {
            .btn-prev, .btn-next {
                height: 35.5px;

                i {
                    line-height: 25px;
                }
            }
            &.disabled {
                position: relative;

                &:before {
                    content: '';
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background: rgba(255, 255, 255, 0.6);
                    z-index: 2;
                }
            }
        }
    }

    .datagrid-header {
        background-color: white;
        color: #4273FA;
        font-weight: 600;
        padding: 1.25rem;
        border: 1px solid #d6dee3;
        border-bottom: 0px;
        position: relative;
        border-top-left-radius: 0.25rem;
        border-top-right-radius: 0.25rem;

        .columns-picker {
           position: absolute;
           right: 30px;
           bottom: -36px;
           z-index: 10;

           .dropdown-menu {
                min-width: 190px;
           }
        }
    }

    .datagrid-body-wrapper {
        overflow-x: hidden;
        background: white;
        padding: 0 1.25rem;
        border: 1px solid #d6dee3;
        border-top: 0px;
        border-bottom-left-radius: 0.25rem;
        border-bottom-right-radius: 0.25rem;


        .loading {
            position: relative;
            overflow: hidden !important;
            z-index: 9;

            &:before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 10;
                background: rgba(255, 255, 255, 0.6);
                //width: 99999px;  // hack: fix position absolute when in scrolled wrapper
                //height: 99999px;
            }

            &:after {
                font-family: "Font Awesome 5 Pro";
                content: "\f1ce";
                color: #485779;
                font-weight: 400;
                font-style: normal;
                font-variant: normal;
                text-rendering: auto;
                font-size: 2rem;
                animation: fa-spin 2s infinite linear;
                position: absolute;
                top: calc(50% - 15px);;
                vertical-align: middle;
                left: calc(50% - 15px);
                z-index: 11;
            }
        }
    }

    .datagrid-body {
        width: 100%;
        max-height: 100px;
        min-height: 100px;
        overflow: auto; /* Povolenie oboch scrollov */
        position: relative;
        margin-bottom: 15px;

        .table {
            width: 100%;
            border-collapse: collapse;
            table-layout: auto;
            margin-bottom: 0 !important;

            thead {
                position: sticky;
                background-color: #e1e8f9;
                z-index: 9;
                top: 0px;
                box-shadow: 0 -8px #f5f8ff;

                th {
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow-x: hidden;
                    /*overflow-x: hidden;*/
                    background: #e1e8f9;
                    font-size: 0.7rem;
                    padding-top: 0px;
                    padding-bottom: 0px;
                    user-select: none;

                    // thead border
                    &:after {
                        content: ' ';
                        bottom: 0px;
                        position: absolute;
                        width: 100%;
                        height: 1px;
                        background: #b9c8de;
                        left: 0;
                        display: block;
                        z-index: 4;
                    }

                    &.fixed:after {
                        display: none !important;
                    }

                    &.checkbox-column {
                        .checkbox-inner-wrapper {
                            margin-top: -10px;
                        }
                    }

                    &.sortable-column {
                        user-select: none;
                        cursor: pointer;
                        overflow: hidden;

                        .sorting-indicator {
                            position: relative;

                            .fad {
                                color: #59698f;
                                font-size: 0.6rem;
                            }
                        }

                        .column-label {
                            overflow: hidden;
                            white-space: nowrap;
                            position: relative;
                            padding-right: 10px;

                            &:before {
                                content: '';
                                position: absolute;
                                top: 0;
                                right: 0;
                                bottom: 0;
                                width: 10px;
                                background: linear-gradient(270deg, rgb(225, 232, 249) 0%, rgba(225, 232, 249, 0) 100%);
                            }
                        }

                        &.active {
                            background-color: #d6def2 !important;

                            .column-label:before {
                                background: linear-gradient(270deg, rgb(214, 222, 242) 0%, rgba(214, 222, 242, 0) 100%);
                            }

                            .sorting-indicator .fad {
                                color: #111;
                            }
                        }
                    }

                    &.fixed-left {
                        position: sticky;
                        z-index: 5;
                        background: #e1e8f9;
                    }

                    &.fixed-left-last {
                        box-shadow: 6px 0 6px -4px rgba(41, 54, 61, 0.18);
                    }

                    &.actions-column {
                        text-align: right;
                        min-width: 120px;

                        &.fixed {
                            position: sticky;
                            right: 0;
                            top: 0px;
                            height: 100%;
                            background: #e1e8f9;
                            z-index: 3;
                        }

                        &.minSize {
                            min-width: 0 !important;
                            width: 60px !important;
                        }

                        &.mobile {
                            min-width: auto !important;
                            width: 60px !important;
                        }

                        .columns-picker-placeholder {
                          height: 35px;
                          width: 55px;
                        }
                    }
                }
            }

            tbody {
                background: white;

                tr {
                    td {
                        background: #fff;

                        .type-money {
                            text-align: right;
                        }
                        .type-date {
                            text-align: center;
                        }

                        // left-pinned columns (grid option fixedColumns): stay put on horizontal
                        // scroll so the row is still identifiable; background comes from the
                        // row rules below (stripe/hover), which apply to every td alike
                        &.fixed-left {
                            position: sticky;
                            z-index: 2;
                        }

                        &.fixed-left-last {
                            box-shadow: 6px 0 6px -4px rgba(41, 54, 61, 0.18);
                        }

                        &.actions-column {
                            text-align: right;
                            white-space: nowrap;

                            &.fixed {
                                position: sticky;
                                right: 0;
                                background: white;
                                z-index: 3;
                                padding-left: 30px;

                                span.gradient {
                                    display: block;
                                    position: absolute;
                                    top: 0;
                                    left: -10px;
                                    width: 10px;
                                    height: 100%;
                                    background: linear-gradient(270deg, white 1%, rgba(255, 255, 255, 0) 100%);
                                }
                            }

                            &.mobile {
                                padding-left: 0px !important;
                            }

                            .item-title {
                                display: none;
                            }


                            .dropdown-toggle {
                                padding: 4px 10px;
                                color: black !important;

                                &:after {
                                    display: none !important;
                                }

                                .fa-fw {
                                    font-size: 1.3rem;
                                }
                            }

                            .open {
                                .dropdown-toggle {
                                    background: #d6def2 !important;
                                    color: black !important;
                                }
                                .dropdown-menu {
                                    z-index: 5;
                                    display: flex;
                                    flex-direction: column;
                                    align-items: start;
                                    justify-content: stretch;
                                    left: auto;
                                    right: 62px;
                                    top: 2px;

                                    .btn {
                                        text-align: left;
                                        padding: 0.5rem 1rem;

                                        .fa, .fas, .far, .fal, .fad, .fab {
                                            margin-right: 1rem;
                                        }

                                        .item-title {
                                            display: inline;
                                        }
                                    }
                                }
                            }

                            a {
                                margin-right: 4px;
                            }
                        }

                        &.checkbox-column {
                            width: 43px;
                            .form-check {
                                margin-top: -7px;
                            }
                        }

                        &.no-data {
                          text-align: center;
                          color: #536c79;
                          user-select: none;
                          background: white !important;
                          font-size: 0.8rem;
                          height: 150px;

                          .msg {
                            max-width: 99vw;
                          }
                        }
                    }

                    &:nth-child(2n) td {
                        background: #F9FBFF;

                        &.actions-column.fixed {
                            background: #F9FBFF;

                            span.gradient {
                                background: linear-gradient(270deg, #F9FBFF 1%, rgba(255, 255, 255, 0) 100%);
                            }
                        }
                    }

                    &:hover td, &:hover td.actions-column.fixed {
                        background: #EBF0FD;

                        span.gradient {
                            background: linear-gradient(270deg, #EBF0FD 1%, #EBF0FD00 100%);
                        }
                    }

                    &.highlighted td:first-child {
                        position: relative;
                        padding-left: calc( 0.5rem + 3px);

                        &.fixed-left {
                            position: sticky;
                        }

                        &:before {
                            content: '';
                            position: absolute;
                            top: 0;
                            left: 0;
                            bottom: 0;
                            width: 3px;
                            z-index: 3;
                        }
                    }

                    &.highlight-muted td { color: #536c79; }
                    &.highlight-primary td:first-child:before { background: #4273fa; }
                    &.highlight-success td:first-child:before { background: #60cf7d; }
                    &.highlight-info td:first-child:before { background: #20a8d8; }
                    &.highlight-secondary td:first-child:before { background: #6c757d; }
                    &.highlight-warning td:first-child:before { background: #ffc107; }
                    &.highlight-danger td:first-child:before { background: #fe3f61; }
                    &.highlight-dark td:first-child:before { background: #29363d; }
                    &.highlight-light td:first-child:before { background: #f0f3f5; }
                }
            }

            // totals row - pinned to the bottom of the scroll area and clearly detached from the
            // data rows (accent top rule + header-like tint), so it never reads as one more employee
            tfoot {
                position: sticky;
                bottom: 0;
                z-index: 8;

                tr.summary-row {
                    td {
                        position: sticky;
                        bottom: 0;
                        background: #e1e8f9;
                        border-top: 2px solid #4273FA;
                        font-weight: 700;
                        color: #29363d;
                        white-space: nowrap;
                        z-index: 2;

                        .type-money {
                            text-align: right;
                        }

                        &.fixed-left {
                            z-index: 3;
                        }

                        &.fixed-left-last {
                            box-shadow: 6px 0 6px -4px rgba(41, 54, 61, 0.18);
                        }

                        &.actions-column.fixed {
                            right: 0;
                            background: #e1e8f9;
                            z-index: 4;

                            span.gradient {
                                display: block;
                                position: absolute;
                                top: 0;
                                left: -10px;
                                width: 10px;
                                height: 100%;
                                background: linear-gradient(270deg, #e1e8f9 1%, rgba(225, 232, 249, 0) 100%);
                            }
                        }
                    }

                    .summary-label {
                        display: flex;
                        align-items: baseline;
                        flex-wrap: nowrap;
                        gap: 8px;

                        .summary-title {
                            color: #4273FA;
                            text-transform: uppercase;
                            letter-spacing: 0.04em;
                            font-size: 0.8rem;
                        }

                        .summary-count {
                            font-weight: 500;
                            font-size: 0.75rem;
                            color: #536c79;
                            background: #fff;
                            border-radius: 10px;
                            padding: 1px 8px;
                        }
                    }

                    // a selection is a different scope than "everything below" - say so
                    &.is-selection td {
                        border-top-color: #60cf7d;

                        .summary-label .summary-title {
                            color: #3f9d57;
                        }
                    }
                }
            }
        }
    }

    .datagrid-footer {
        position: relative;
        background: #f5f8ff;
        padding: 0 8px;
        font-size: 0.8rem;
        display: flex;
        align-items: center;
        min-height: 44px;
        border-bottom-left-radius: 0.25rem;
        border-bottom-right-radius: 0.25rem;

        .batch-actions-panel {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            background-color: #e1e8f9;
            border-radius: 3px;
            margin: -3px 10px 0 20px;
            padding-left: 10px;

            .form-check { margin-bottom: 0px; }
            .form-check-label { margin-bottom: 15px; }
        }

        // problem so scrollbarmi v rôznych OS/browseroch... niektoré sú širšie, iné užšie a toto ich prekrýva
        /*&:before {
            content: '';
            display: block;
            position: absolute;
            top: -24px;
            left: 1px;
            right: 1px;
            height: 25px;
            background: linear-gradient(0deg, white 1%, rgba(255, 255, 255, 0) 100%);
            border-bottom: solid #d6dee3 1px;
            border-bottom-left-radius: 0.25rem;
            border-bottom-right-radius: 0.25rem;
            z-index: 3;
        }*/
    }
</style>



// WEBPACK FOOTER //
// resources/assets/admin/js/components/datagrid/Datagrid.vue